import React from "react";
import { safeJSONParse } from "../../utils/helper";
import { MessageInputHandle } from "../ChatWindow/MessageInput";
import UtilityAgent from "./UtilityAgent";
import PressReleaseAgent from "./PressReleaseAgent";
import PressReleaseQAAgent from "./PressReleaseQAAgent";
import LocalizerAgent from "./LocalizerAgent";
import TranslationAgent from "./TranslationAgent";
import MediaBriefAgent from "./MediaBriefAgent";
import SocialMediaInsightsAgent from "./SocialMediaInsightsAgent";
import InternalCommunicationAgent from "./InternalCommunicationAgent";
import WorkflowEndAgent from "./WorkflowEndAgent";
import PrMbWorkflowEndAgent from "./PrMbWorkflowEndAgent";
import SyntheticResumeAgent from "./SyntheticResumeAgent";
import ResumeFetcherAgent from "./ResumeFetcherAgent";
import ScoringAgent from "./ScoringAgent";
import EmailDraftingAgent from "./EmailDraftingAgent";
import HrAgentV2HitlAgent from "./HrAgentV2HitlAgent";
import ValidationCheckAgent from "./ValidationCheckAgent";
import NoCandidatesFoundAgent from "./NoCandidatesFoundAgent";
import TemplateReviewerAgent from "./TemplateReviewerAgent";
import ContractReviewerAgent from "./ContractReviewerAgent";
import TemplateReviewerIntakeAgent from "./TemplateReviewerIntakeAgent";
import TemplateReviewerChecklistAgent from "./TemplateReviewerChecklistAgent";
import RedrafterOrchestratorAgent from "./RedrafterOrchestratorAgent";
import TemplateGuidedIntakeAgent from "./TemplateGuidedIntakeAgent";
import SocialMediaIntakeAgent from "./SocialMediaIntakeAgent";
import OrchestratorAgent from "./OrchestratorAgent";
import JDComposerAgent from "./JDComposerAgent";
import JDDetailsAgent from "./JDDetailsAgent";
import InferenceWorkflowEndAgent from "./InferenceWorkflowEndAgent";
import TemplateDocumentSelectorAgent from "./TemplateDocumentSelectorAgent";
import DataTalkIntentClassifierAgent from "./DataTalkIntentClassifierAgent";
import DataTalkQueryPlannerAgent from "./DataTalkQueryPlannerAgent";
import DataTalkSqlGeneratorAgent from "./DataTalkSqlGeneratorAgent";
import SowAutoFillerAgent from "./SowAutoFillerAgent";
import AnswerComposerAgent from "./AnswerComposerAgent";
import LegalReviewerAgent from "./LegalReviewerAgent";
import RfqOrchestratorAgent from "./RfqOrchestratorAgent";
import RfqBuilderAgent from "./RfqBuilderAgent";
import RfqResponseEvaluatorAgent from "./RfqResponseEvaluatorAgent";
import HrAgentResponse from "./HrAgentResponse";
import CVScreenerAgentV2 from "./CVScreenerAgentV2";
import SkillGapAssessmentAgent from "./SkillGapAssessmentAgent";
import SkillIntelligenceAgent from "./SkillIntelligenceAgent";
import QuestionGenerationAgent from "./QuestionGenerationAgent";
import InterviewIntakeResponse from "./InterviewIntakeResponse";
import JobIntakeAgent from "./JobIntakeAgent";
import TalentReportAgent from "./TalentReportAgent";
import TrainingSearchAgent from "./TrainingSearchAgent";
import FrameworkApprovalAgent from "./FrameworkApprovalAgent";
import EvaluationResultsAgent from "./EvaluationResultsAgent";
import LearningPathAgent from "./LearningPathAgent";
import LearningPathAgentV2 from "./LearningPathAgentV2";
import LearningPathUtilityAgentForm from "./LearningPathUtilityAgentForm";

type AgentResponseProps = {
  agentId: string | undefined;
  content: string;
  inputRef?: React.RefObject<MessageInputHandle>;
  currentChat?: any[];
  messageId?: string;
};

export default function AgentResponse({
  agentId,
  content,
  inputRef,
  currentChat = [],
  messageId,
}: AgentResponseProps): JSX.Element {
  const parsedContent = safeJSONParse(content);

  // Handler for suggestion click - shared across multiple agents
  const handleSuggestionClick = (suggestion: string) => {
    if (inputRef?.current) {
      inputRef.current.sendPrompt(suggestion);
    }
  };

  console.log("PARSED CONTENT", parsedContent);
  console.log("AGENT", agentId);

  // Handle backend errors gracefully
  if (!agentId && parsedContent?.error) {
    return (
      <div style={{ padding: "16px", width: "100%" }}>
        <div
          style={{
            padding: "16px",
            backgroundColor: "#fef2f2",
            borderRadius: "8px",
            border: "1px solid #fca5a5",
            marginBottom: "16px",
          }}
        >
          <div style={{ fontSize: "15px", fontWeight: 600, color: "#991b1b", marginBottom: "8px" }}>
            ⚠️ Error
          </div>
          <div
            style={{
              fontSize: "13px",
              color: "#7f1d1d",
              lineHeight: "1.6",
            }}
          >
            {parsedContent.error || "An error occurred. Please try again."}
          </div>
          {parsedContent.error_id && (
            <div
              style={{
                fontSize: "11px",
                color: "#991b1b",
                marginTop: "8px",
                fontFamily: "monospace",
              }}
            >
              Error ID: {parsedContent.error_id}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (agentId === "utility_agent") {
    return <UtilityAgent parsedContent={parsedContent} />;
  }

  if (agentId === "press_release_agent") {
    return (
      <PressReleaseAgent
        parsedContent={parsedContent}
        handleSuggestionClick={handleSuggestionClick}
      />
    );
  }

  if (
    agentId === "press_release_qa_agent" ||
    agentId === "PressReleaseQAAgent"
  ) {
    return <PressReleaseQAAgent parsedContent={parsedContent} />;
  }

  if (agentId === "LocalizerAgent" || agentId === "localizer_agent") {
    return (
      <LocalizerAgent
        parsedContent={parsedContent}
        handleSuggestionClick={handleSuggestionClick}
      />
    );
  }

  if (agentId == "orchestrator_new_agent") {
    return (
      <OrchestratorAgent
        parsedContent={parsedContent}
      />
    )
  }

  if (agentId === "media_brief_agent") {
    return (
      <MediaBriefAgent
        parsedContent={parsedContent}
        handleSuggestionClick={handleSuggestionClick}
      />
    );
  }

  if (agentId === "social_media_insights_agent") {
    return <SocialMediaInsightsAgent parsedContent={parsedContent} />;
  }

  if (agentId === "internal_communication_agent") {
    return (
      <InternalCommunicationAgent
        parsedContent={parsedContent}
        handleSuggestionClick={handleSuggestionClick}
      />
    );
  }

  if (agentId === "workflow_end") {
    return <WorkflowEndAgent parsedContent={parsedContent} currentChat={currentChat} />;
  }

  if (agentId === "pr_mb_workflow_end") {
    return <PrMbWorkflowEndAgent parsedContent={parsedContent} currentChat={currentChat} />;
  }

  if (agentId === "inference_workflow_end") {
    return <InferenceWorkflowEndAgent parsedContent={parsedContent} />;
  }

  if (
    agentId === "synthetic_resume_agent" ||
    agentId === "skill_inference_agent" ||
    agentId === "modify_requirements"
  ) {
    return <SyntheticResumeAgent parsedContent={parsedContent} agentId={agentId} />;
  }

  if (agentId == "jd_details_agent") {
    return <JDDetailsAgent parsedContent={parsedContent} />
  }

  if (agentId == "intent_classifier_agent") {
    return <DataTalkIntentClassifierAgent parsedContent={parsedContent} handleSuggestionClick={handleSuggestionClick} />
  }

  if (agentId == "query_planner_agent") {
    return <DataTalkQueryPlannerAgent parsedContent={parsedContent} />
  }

  if (agentId == "sql_generator_agent") {
    return <DataTalkSqlGeneratorAgent parsedContent={parsedContent} />
  }

  if (agentId === "jd_composer_agent") {
    return <JDComposerAgent parsedContent={parsedContent} />
  }
  if (agentId === "resume_fetcher_agent") {
    return <ResumeFetcherAgent parsedContent={parsedContent} />;
  }

  if (agentId === "scoring_agent" || agentId === "scoring_agent_new") {
    return <ScoringAgent parsedContent={parsedContent} agentId={agentId} />;
  }

  if (agentId === "email_drafting") {
    return <EmailDraftingAgent parsedContent={parsedContent} />;
  }

  if (agentId === "validation_check") {
    return <ValidationCheckAgent parsedContent={parsedContent} />;
  }

  if (agentId === "hr_agent_v2_hitl" || agentId === "role_validation_failed") {
    return <HrAgentV2HitlAgent parsedContent={parsedContent} />;
  }

  if (agentId === "no_candidates_found") {
    return <NoCandidatesFoundAgent parsedContent={parsedContent} />;
  }

  if (agentId === "translation_agent") {
    return (
      <TranslationAgent
        parsedContent={parsedContent}
        handleSuggestionClick={handleSuggestionClick}
      />
    );
  }

  if (
    agentId === "template_reviewer_intake_agent" ||
    agentId === "template_reviewer_qa_agent"
  ) {
    return <TemplateReviewerIntakeAgent parsedContent={parsedContent} />;
  }

  if (
    agentId === "template_guided_intake_agent"
  ) {
    return <TemplateGuidedIntakeAgent parsedContent={parsedContent} />;
  }

  if (agentId === "template_reviewer_agent") {
    return <TemplateReviewerAgent parsedContent={parsedContent} />;
  }

  if (agentId === "template_reviewer_checklist_agent") {
    return <TemplateReviewerChecklistAgent parsedContent={parsedContent} messageId={messageId || ""} />;
  }

  if (agentId === "template_document_selector_agent") {
    return <TemplateDocumentSelectorAgent parsedContent={parsedContent} />;
  }

  if (
    agentId === "contract_reviewer_orchestrator" ||
    agentId === "contract_reviewer_qa_agent" ||
    agentId === "ConsolidatorAgent" ||
    agentId === "consolidator_agent" ||
    agentId === "redrafting_agent"
  ) {
    return <ContractReviewerAgent parsedContent={parsedContent} />;
  }

  if (agentId === "legal_reviewer_orchestrator") {
    return <LegalReviewerAgent parsedContent={parsedContent} />;
  }

  if (agentId === "redrafter_orchestrator") {
    return <RedrafterOrchestratorAgent parsedContent={parsedContent} />;
  }

  if (agentId === "socialmedia_intake_agent") {
    return <SocialMediaIntakeAgent parsedContent={parsedContent} />;
  }

  if (agentId === "sow_auto_filler") {
    return <SowAutoFillerAgent parsedContent={parsedContent} messageId={messageId} />;
  }

  if (agentId === "answer_composer_agent") {
    return <AnswerComposerAgent parsedContent={parsedContent} />;
  }

  if (agentId === "rfq_orchestrator" || agentId === "criteria_parser") {
    return <RfqOrchestratorAgent parsedContent={parsedContent} />;
  }

  if (agentId === "rfq_builder") {
    return <RfqBuilderAgent parsedContent={parsedContent} />;
  }

  if (agentId === "rfq_response_evaluator") {
    return <RfqResponseEvaluatorAgent parsedContent={parsedContent} />;
  }

  // CV Screener V2 Agent (Agent ID: 35)
  if (
    agentId === "cv_screener_v2" ||
    agentId === "cv_screener_v2_hitl" ||
    agentId === "cv_screener_response"
  ) {
    return <CVScreenerAgentV2 parsedContent={parsedContent} />;
  }

  // Skill Gap Assessment Agent (Agent ID: 38)
  if (agentId === "skill_gap_assessment" || agentId === "skill_gap_response") {
    return <SkillGapAssessmentAgent parsedContent={parsedContent} />;
  }

  // Skill Intelligence Agent / Human Capital Skill Intelligence (Agent ID: 40)
  if (
    agentId === "skill_intelligence" ||
    agentId === "skill_intelligence_agent" ||
    agentId === "skill_intelligence_response" ||
    agentId === "skill_mapping_agent" ||
    agentId === "skill_mapping_executor" ||
    agentId === "skill_mapping_agent_modify" ||
    agentId === "skill_mapping_agent_download" ||
    agentId === "human_capital_skill_intelligence" ||
    agentId === "human_capital_agent"
  ) {
    return <SkillIntelligenceAgent parsedContent={parsedContent} />;
  }

  // Interview Questions Agent (Agent 41) - Specific UI
  if (
    agentId === "question_generation_agent" ||
    agentId === "interview_question_design"
  ) {
    return <QuestionGenerationAgent parsedContent={parsedContent} />;
  }

  // Interview Intake Response
  if (agentId === "interview_intake_response") {
    return <InterviewIntakeResponse parsedContent={parsedContent} />;
  }

  // Job Intake Intelligence Agent (Agent 37) - Specific UI
  if (
    agentId === "job_intake_response" ||
    agentId === "job_intake_agent"
  ) {
    return <JobIntakeAgent parsedContent={parsedContent} />;
  }

  // Framework Approval Agent - CV Screener V2 approval workflow
  if (agentId === "framework_approval_agent") {
    return <FrameworkApprovalAgent parsedContent={parsedContent} />;
  }

  // Evaluation Results Agent - CV Screener V2 results display
  if (agentId === "evaluation_results_agent") {
    return <EvaluationResultsAgent parsedContent={parsedContent} />;
  }


  // Learning Path Utility Agent Form - Intake form with skill gaps (after clicking "Proceed")
  if (agentId === "learning_path_utility_agent_form") {
    return <LearningPathUtilityAgentForm parsedContent={parsedContent} messageId={messageId} />;
  }

  // Learning Path Agent - AI Skill Assessment and Learning Pathway
  if (
    agentId === "learning_path_agent" ||
    agentId === "learning_path_response" ||
    agentId === "learning_path_intake" ||
    agentId === "learning_path_generator"
  ) {
    return <LearningPathAgent parsedContent={parsedContent} />;
  }

  // Talent Scouting Agent (Agent 39) - Specific UI
  if (
    agentId === "talent_scouting_response" ||
    agentId === "talent_report_agent" ||
    agentId === "report_agent" ||
    agentId === "utility_agent"
  ) {
    return <TalentReportAgent parsedContent={parsedContent} />;
  }

  // Training Search Agent (Agent 42) - Specific UI
  if (
    agentId === "training_search_response" ||
    agentId === "training_search_agent" ||
    agentId === "training_search_finalize" ||
    agentId === "training_search_preferences_form" ||
    agentId === "training_search_chat" ||
    agentId === "training_search_error"
  ) {
    return <TrainingSearchAgent parsedContent={parsedContent} />;
  }

  return (
    <div>
      <div>Something went wrong, please try again!</div>
    </div>
  );
}
